import InputError from "@/Components/UI/Forms/InputError";
import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import PromoCodeBadge from "@/Components/UI/Onboarding/Client/PromoCodeBadge";
import { TrialTimelinePricingCard } from "@/Components/UI/Onboarding/Client/TrialTimelinePricingCard";
import TrustBadges from "@/Components/UI/Onboarding/Client/TrustBadges";
import { V11PricingCard } from "@/Components/UI/Onboarding/Client/V11PricingCard";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import { Client, None, PageProps } from "@/types/page";
import { Price } from "@/types/price";
import dataLayerUserData from "@/utils/dataLayer";
import { coachWithCerts } from "@/utils/fullUserNames";
import { formatMoneyWithCurrency } from "@/utils/moneyFormatting";
import pluralize from "@/utils/plural";
import { ScrolledIntoViewTracker } from "@/utils/Visibility";
import { useForm, usePage } from "@inertiajs/react";
import * as Sentry from "@sentry/react";
import {
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import {
    FormEventHandler,
    FunctionComponent,
    useContext,
    useRef,
    useState,
} from "react";
import { FiLoader } from "react-icons/fi";
import { RiLock2Fill } from "react-icons/ri";
import {
    BetweenPricingAndPaymentProps,
    PaymentFormData,
    PurchasePixelDecisionResponse,
} from "./types";

export function CheckoutForm({
    paymentFormData,
    selectedPrice,
    onPaymentComplete,
    BetweenPricingAndPaymentComponent,
}: {
    paymentFormData: PaymentFormData;
    selectedPrice?: Price;
    onPaymentComplete?: () => void;
    BetweenPricingAndPaymentComponent?: FunctionComponent<BetweenPricingAndPaymentProps>;
}) {
    const { trialDays, setupIntent, promotionCode, coach } = paymentFormData;
    const { user } = usePage<PageProps<None, Client>>().props;
    const showsTrialTimeline =
        paymentFormData.trialTimelineVariant === "treatment" && trialDays > 0;
    const analytics = useContext(AnalyticsContext);
    const stripe = useStripe();
    const elements = useElements();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stripeError, setStripeError] = useState("");

    const addPaymentMethodForm = useForm({
        payment_method_id: "",
        stripePriceId: selectedPrice?.stripePriceId,
    });

    const attemptNumber = useRef(1);

    const pushSubscribedEvent = async (price: Price) => {
        let decision: PurchasePixelDecisionResponse | null = null;
        // A failed or slow decision request still sends the Purchase so a network blip never costs us an ad conversion.
        try {
            const { data } =
                await window.axios.post<PurchasePixelDecisionResponse>(
                    route("checkout-confirmation"),
                    undefined,
                    { timeout: 5000 },
                );
            decision = data;
        } catch (error) {
            Sentry.captureException(error, {
                tags: { feature: "purchase_pixel_decision" },
            });
        }

        dataLayer.push({
            event: "subscribed",
            currency: "usd",
            value: price.amount / 100,
            event_id: decision?.eventId,
            send_purchase_pixel: decision?.shouldSendPurchasePixel ?? true,
            ...dataLayerUserData(user),
        });
    };

    const addPaymentMethodFormSubmit: FormEventHandler = async (e) => {
        if (!selectedPrice) {
            Sentry.captureException("No selected price in CheckoutForm");
            return;
        }
        e.preventDefault();

        if (!stripe || !elements) {
            Sentry.captureException("Stripe.js not loaded");
            // Stripe.js hasn't yet loaded.
            return;
        }

        setIsSubmitting(true);

        const stripeSubmitCheck = await elements?.submit();

        if (stripeSubmitCheck.error) {
            const errorMessage = stripeSubmitCheck.error.message ?? "";
            Sentry.captureException(`Stripe error: ${errorMessage}`);
            setIsSubmitting(false);
            return;
        }

        const setupConfirmation = await stripe.confirmSetup({
            elements: elements,
            clientSecret: setupIntent?.client_secret ?? "",
            redirect: "if_required",
        });

        if (setupConfirmation.error) {
            const errorMessage = setupConfirmation.error.message ?? "";
            analytics.trackEvent("payment_failed_checkout", {
                type: "error",
                extra: errorMessage,
                attempt_number: attemptNumber.current,
                tried_dollar_hold: false,
            });
            attemptNumber.current++;
            Sentry.captureException(`Stripe error: ${errorMessage}`);
            setStripeError(errorMessage);
            setIsSubmitting(false);
            return;
        }

        if (setupConfirmation.setupIntent.status !== "succeeded") {
            analytics.trackEvent("payment_failed_checkout", {
                type: "status",
                extra: setupConfirmation.setupIntent.status,
                attempt_number: attemptNumber.current,
                tried_dollar_hold: false,
            });
            attemptNumber.current++;
            Sentry.captureException(
                "Setup intent not succeeded, status: " +
                    setupConfirmation.setupIntent.status,
            );
            setStripeError(
                "Error processing payment method, status not succeeded",
            );
            setIsSubmitting(false);
            return;
        }

        if (!setupConfirmation.setupIntent.payment_method) {
            analytics.trackEvent("payment_failed_checkout", {
                type: "missing_payment_method",
                extra: "",
                attempt_number: attemptNumber.current,
                tried_dollar_hold: false,
            });
            attemptNumber.current++;
            Sentry.captureException("No payment method returned from Stripe");
            setStripeError("Error processing payment method");
            setIsSubmitting(false);
            return;
        }

        addPaymentMethodForm.data.payment_method_id =
            setupConfirmation.setupIntent.payment_method.toString();
        addPaymentMethodForm.data.stripePriceId = selectedPrice.stripePriceId;

        addPaymentMethodForm.post(route("client.paymentMethod"), {
            preserveScroll: true,
            onFinish: () => {
                setIsSubmitting(false);
            },
            onSuccess: () => {
                // if you are uncommenting this, you better know what you are doing
                // talk to Alon and Alain first
                //
                // if (trialDays && trialDays > 0) {
                //     dataLayer.push({
                //         event: "trial_started",
                //         ...dataLayerUserData(user),
                //     });
                // } else {
                pushSubscribedEvent(selectedPrice);
                // }
                onPaymentComplete?.();
            },
            onError: (errors) => {
                if (errors.payment_method_id) {
                    analytics.trackEvent("payment_failed_checkout", {
                        type: trialDays
                            ? "server_dollar_hold_fail"
                            : "server_subscription_start_fail",
                        extra: errors.payment_method_id,
                        attempt_number: attemptNumber.current,
                        tried_dollar_hold: true,
                    });
                    // the error message is already displayed in the form's InputError below
                    setStripeError("");
                }
            },
        });
    };

    const computeDiscountedCents = (
        originalCents: number,
        percentOff: number,
        amountOffCents: number,
    ): number => {
        let discountedCents = originalCents;
        if (percentOff > 0) {
            discountedCents = Math.round(
                (originalCents * (100 - percentOff)) / 100,
            );
        }
        if (amountOffCents > 0) {
            discountedCents = discountedCents - amountOffCents;
        }
        return Math.max(0, discountedCents);
    };

    let belowPaymentFormText = "";
    if (selectedPrice && trialDays === 0) {
        belowPaymentFormText = `You'll be charged ${selectedPrice.amountForDisplay} today for your first ${pluralize(selectedPrice.intervalCount, selectedPrice.interval)}. After that, you will pay ${selectedPrice.amountPerSessionForDisplay}/week, billed as ${selectedPrice.amountForDisplay} every ${pluralize(selectedPrice.intervalCount, selectedPrice.interval)}. You can change plans or cancel anytime.`;
    } else if (selectedPrice) {
        let originalCents =
            paymentFormData.pricingVersion.trialCostAmountCents ?? 0;
        if (paymentFormData.pricingVersion.trialCostAmountCentsPerPriceGroup) {
            originalCents =
                paymentFormData.pricingVersion
                    .trialCostAmountCentsPerPriceGroup[
                    selectedPrice.priceGroup ?? "normal"
                ] ?? 0;
        }
        const percentOff = promotionCode?.coupon?.percentOff ?? 0;
        const amountOffCents = promotionCode?.coupon?.amountOff ?? 0;

        const discountedCents = computeDiscountedCents(
            originalCents,
            percentOff,
            amountOffCents,
        );

        const costTodayForDisplay = formatMoneyWithCurrency(
            discountedCents,
            user.currency ?? "usd",
        );
        belowPaymentFormText = `You will be charged ${costTodayForDisplay} today for your 1 week trial. After the trial, you will pay ${selectedPrice.amountPerSessionForDisplay}/week, billed as ${selectedPrice.amountForDisplay} every ${pluralize(selectedPrice.intervalCount, selectedPrice.interval)}. We'll send a reminder email 2 days before your trial ends. Change plans or cancel anytime.`;
    }

    return (
        <form
            data-testid="payment-form-summary"
            onSubmit={addPaymentMethodFormSubmit}
            className="bg-brand-white px-4 sm:px-8 pb-8 pt-2 sm:max-w-xl"
        >
            {coach ? (
                <div className="mb-4 flex items-center justify-center pt-4 text-xl font-bold text-brand-black">
                    Start coaching with {coachWithCerts(coach)}
                </div>
            ) : (
                <div className="mb-4 flex items-center justify-center pt-4 text-center text-xl font-bold text-brand-black">
                    Start Your Plan and
                    <br />
                    {paymentFormData.entrepreneurshipOnlyCoachVariant ===
                    "treatment"
                        ? "Get Paired with a Business Coach"
                        : "Get Paired with a Coach"}
                </div>
            )}

            {selectedPrice &&
                (showsTrialTimeline ? (
                    <TrialTimelinePricingCard
                        price={selectedPrice}
                        pricingVersion={paymentFormData.pricingVersion}
                        promotionCode={promotionCode}
                        client={user}
                        trialDays={trialDays}
                    />
                ) : (
                    <V11PricingCard
                        price={selectedPrice}
                        pricingVersion={paymentFormData.pricingVersion}
                        promotionCode={promotionCode}
                        client={user}
                        trialDays={trialDays}
                    />
                ))}
            {selectedPrice &&
                promotionCode &&
                !paymentFormData.pricingVersion.hidePromoCodeBadge && (
                    <PromoCodeBadge
                        price={selectedPrice}
                        promotionCode={promotionCode}
                        client={user}
                    />
                )}
            {BetweenPricingAndPaymentComponent && (
                <BetweenPricingAndPaymentComponent
                    paymentFormData={paymentFormData}
                />
            )}
            <div className="my-4"></div>
            <div>
                <div>
                    <div className="rounded-lg border p-4 pt-3">
                        <ScrolledIntoViewTracker eventName="payment_box_scrolled_into_view">
                            <PaymentElement
                                options={{
                                    terms: {
                                        applePay: "never",
                                        googlePay: "never",
                                        card: "never",
                                        auBecsDebit: "never",
                                        bancontact: "never",
                                        cashapp: "never",
                                        ideal: "never",
                                        paypal: "never",
                                        sepaDebit: "never",
                                        sofort: "never",
                                        usBankAccount: "never",
                                    },
                                    defaultValues: {
                                        billingDetails: {
                                            name:
                                                user.first_name +
                                                " " +
                                                user.last_name,
                                            email: user.email,
                                            phone: user.phone ?? "",
                                        },
                                    },
                                }}
                            />
                        </ScrolledIntoViewTracker>
                    </div>
                    <div className="mt-2 flex items-center justify-start text-sm text-brand-gray">
                        <RiLock2Fill className="mr-1 text-brand-green" />
                        Encrypted
                    </div>
                </div>

                <InputError
                    className="mt-4 border border-red-500 p-1 text-center text-xl font-bold"
                    message={addPaymentMethodForm.errors.payment_method_id}
                />

                <InputError message={stripeError} />

                <PrimaryButton
                    type="submit"
                    className="mt-4 flex w-full min-w-40 items-center justify-center bg-blue-500 px-4 py-3 font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                    disabled={
                        addPaymentMethodForm.processing ||
                        !stripe ||
                        isSubmitting ||
                        !selectedPrice
                    }
                >
                    {addPaymentMethodForm.processing || isSubmitting ? (
                        <FiLoader className="animate-spin" size={18} />
                    ) : (
                        <div className="flex items-center">
                            <div className="ml-2">Start Coaching</div>
                        </div>
                    )}
                </PrimaryButton>
                <div
                    data-testid="pricing-footer-description"
                    className="mt-4 text-sm text-brand-black"
                >
                    {selectedPrice ? (
                        belowPaymentFormText
                    ) : (
                        <div className="text-center">
                            <button
                                className="text-brand-red underline"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const top =
                                        (document
                                            .getElementById(
                                                "coach-selection-container",
                                            )
                                            ?.getBoundingClientRect()?.top ??
                                            0) +
                                        window.scrollY -
                                        50;
                                    if (top) {
                                        window.scrollTo({
                                            top: top,
                                            behavior: "smooth",
                                        });
                                    }
                                }}
                            >
                                Select a coach
                            </button>{" "}
                            above to continue
                        </div>
                    )}
                </div>
                <TrustBadges />
            </div>
        </form>
    );
}
